import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AdminClaimActionResponse,
  AdminClaimApproveResponse,
  AdminClaimProcessRequest,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { apiClient } from '@/lib/apiClient';
import type { ClaimActionType } from '../lib/claimActionRbac';
import { claimQueryKey } from './useClaimQuery';

const ACTION_TO_PATH: Record<
  ClaimActionType,
  | '/api/admin/v1/claims/{claimId}/approve'
  | '/api/admin/v1/claims/{claimId}/reject'
  | '/api/admin/v1/claims/{claimId}/collect'
  | '/api/admin/v1/claims/{claimId}/complete'
> = {
  approve: '/api/admin/v1/claims/{claimId}/approve',
  reject: '/api/admin/v1/claims/{claimId}/reject',
  collect: '/api/admin/v1/claims/{claimId}/collect',
  complete: '/api/admin/v1/claims/{claimId}/complete',
};

export interface ClaimActionInput {
  claimId: string;
  action: ClaimActionType;
  body: AdminClaimProcessRequest;
}

export type ClaimActionResult = AdminClaimActionResponse | AdminClaimApproveResponse;

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, { status, ...(env.meta ? { meta: env.meta } : {}) });
  }
  throw new TransportError(`Claim action failed with status ${status}`, raw);
};

export const useClaimActionMutation = () => {
  const qc = useQueryClient();
  return useMutation<ClaimActionResult, Error, ClaimActionInput>({
    mutationFn: async ({ claimId, action, body }) => {
      const path = ACTION_TO_PATH[action];
      const concretePath = path.replace('{claimId}', claimId);
      const idempotencyKey = idempotencyKeyFor('POST', concretePath, body);
      const { data, error, response } = await apiClient.raw.POST(path, {
        body,
        params: {
          path: { claimId },
          header: { 'Idempotency-Key': idempotencyKey },
        },
      });
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: ClaimActionResult }).data;
    },
    onSuccess: async (_data, { claimId }) => {
      await qc.invalidateQueries({ queryKey: claimQueryKey(claimId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'claims'] });
    },
  });
};

export const isApproveResponse = (
  result: ClaimActionResult,
): result is AdminClaimApproveResponse =>
  typeof (result as AdminClaimApproveResponse).refundTriggeredAt === 'string';
